import { action, computed, observable } from 'mobx'
import { QuestionAnswerEditorViewModelBase } from '@/features/jobs/questions/QuestionAnswerEditorViewModelBase'
import {
  type MultiSelectAnswer,
  type SelectQuestion,
  type SingleSelectAnswer,
} from '@/features/jobs/questions/questions'
import { shallowItemsAreEqual } from '@/utils/array-util'
import { QuestionValidationResult } from '@/features/jobs/questions/question-validation-result'

export class SelectQuestionAnswerEditorViewModel extends QuestionAnswerEditorViewModelBase<SelectQuestion> {
  /** @inheritDoc */
  readonly type = 'Select'

  @observable.ref accessor value: readonly string[] = []
  @observable accessor multi = false
  @observable accessor options: SelectQuestion['options'] = []

  /** @inheritDoc */
  @computed get isAnswered(): boolean {
    return this.value.length > 0
  }

  /** @inheritDoc */
  @computed get answer(): SingleSelectAnswer | MultiSelectAnswer | null {
    if (this.value.length === 0) {
      return null
    }

    if (this.multi) {
      return {
        type: 'MultiSelectAnswer',
        value: this.value.slice(),
      }
    }

    return {
      type: 'SingleSelectAnswer',
      value: this.value[0],
    }
  }

  /** @inheritDoc */
  @computed get textAnswer() {
    if (this.value.length === 0) {
      return null
    }

    const selectedOptions = this.value
      .map((id) => this.options.find((opt) => opt.id === id)?.text)
      .filter(Boolean)

    return selectedOptions.join(', ')
  }

  /** @inheritDoc */
  @action.bound
  respond(value: string[]) {
    if (shallowItemsAreEqual(this.value, value)) {
      return
    }

    this.value = value
    this.notifyAnswerChanged()
    if (!this.multi) {
      this.notifyContinue()
    }
  }

  /** @inheritDoc */
  set(question: SelectQuestion): void {
    this.multi = question.multi
    this.options = question.options.slice()

    if (question.answer) {
      this.value = Array.isArray(question.answer.value)
        ? question.answer.value
        : [question.answer.value]
    } else {
      this.value = []
    }
  }

  /** @inheritDoc */
  validate(): QuestionValidationResult {
    if (!this.answer) {
      return QuestionValidationResult.failed(['Please select an option.'])
    }

    return QuestionValidationResult.ok()
  }
}
